import { formatDate } from '@angular/common';
import { Component, EventEmitter, HostListener, Inject, Input, LOCALE_ID, Output, ViewChild } from '@angular/core';
import { ModuleManagerDateRangeInfo } from 'src/shared/models/module-manager/module-manager-settings.models';
import { BaremetricsCalendar } from '../baremetrics-calendar/baremetrics-calendar.component';
import { CvConfigurationService } from '../services/cv-configuration.service';
import { CVLocalizationService } from '../services/cv-localization.service';
import SlideoutSettingsManager from '../../../shared/models/slideout-settings-manager';


@Component({
    selector: 'cv-quick-date-range-selector',
    templateUrl: './quick-date-range-selector.component.html',
    styleUrls: ['./quick-date-range-selector.component.scss']
})
export class QuickDateRangeSelectorComponent {

    @Input() maxDays: number = null;
    @Input() isHidden: boolean = false;
    @Input() pageName?: string = null;
    @Input() minDate = new Date().addMonths(-25);
    @Input() settingsManager: SlideoutSettingsManager;

    @Output() selectedDateRangeChange = new EventEmitter<ModuleManagerDateRangeInfo>();

    _availableDateRanges: ModuleManagerDateRangeInfo[];
    get availableDateRanges() { return this._availableDateRanges; }
    @Input() set availableDateRanges(val: ModuleManagerDateRangeInfo[]) {
        if (!val)
            return;

        this.isInitialized = true;
        this._availableDateRanges = val;
        if(!this.config.isCxOne)
            this.setDateRanges();
    }

    _selectedDateRange: ModuleManagerDateRangeInfo;
    get selectedDateRange() { return this._selectedDateRange; }
    @Input() set selectedDateRange(val: ModuleManagerDateRangeInfo) {
        if (!val)
            return;

        if (this._selectedDateRange?.name === val.name)
            return;

        if (val.name.includes(' - ')) {
            this.selectedCustomRange = ModuleManagerDateRangeInfo.fromObject(val);
            this.selectedStartDate = val.startDate;
            this.selectedEndDate = val.endDate;
        }

        this._selectedDateRange = val;
    }

    _changeAllowed = false;
    get changeAllowed() { return this._changeAllowed; }
    @Input() set changeAllowed(val: boolean) {
        this._changeAllowed = val;
    }

    // Days, Weeks, Months, Quarters, Years
    dayDateRanges: ModuleManagerDateRangeInfo[];
    weekDateRanges: ModuleManagerDateRangeInfo[];
    monthDateRanges: ModuleManagerDateRangeInfo[];
    quarterDateRanges: ModuleManagerDateRangeInfo[];
    yearDateRanges: ModuleManagerDateRangeInfo[];
    customDateRange: ModuleManagerDateRangeInfo;

    CVAngularDateFormat: string;
    selectedStartDate: Date;
    selectedEndDate: Date;
    selectedCustomRange: ModuleManagerDateRangeInfo;
    isInitialized = false;
    showDropDown = false;
    showCustom = false;
    maxDaysError = '';

    private clickedInside = false;

    constructor(
        @Inject(LOCALE_ID) private locale: string,
        @Inject('$window') $window: Window,
        private cvLocalizationService: CVLocalizationService,
        public config: CvConfigurationService,
    ) {
        this.CVAngularDateFormat = $window["CVAngularDateFormat"];
    }

    @HostListener('click')
    clickInside() {
        this.clickedInside = true;
    }

    @HostListener('document:click')
    clickOutside() {
        if (!this.clickedInside && (this.showDropDown || this.showCustom)) {
            this.showDropDown = false;
            this.showCustom = false;
        }
        this.clickedInside = false;
    }

    onMainClick() {
        this.showDropDown = !this.showDropDown;
    }

    onCustomDateRangeChanged(calendar: BaremetricsCalendar) {
        this.selectedStartDate = calendar.start_date.toDate();
        this.selectedEndDate = calendar.end_date.toDate();
        const formattedStart = formatDate(this.selectedStartDate, this.CVAngularDateFormat, this.locale);
        const formattedEnd = formatDate(this.selectedEndDate, this.CVAngularDateFormat, this.locale);
        this.selectedCustomRange.name = `${formattedStart} - ${formattedEnd}`;
        this.checkMaxDays();
    }

    setDateRanges(){
        this.dayDateRanges = [];
        this.weekDateRanges = [];
        this.monthDateRanges = [];
        this.quarterDateRanges = [];
        this.yearDateRanges = [];
        this.customDateRange = new ModuleManagerDateRangeInfo();

        this._availableDateRanges.forEach(range => {
            if(range.name === "Today"
            || range.name === "Yesterday"
            || range.name.includes("Days"))
                this.dayDateRanges.push(range);
            else if(range.name.includes("Week")
            || range.name.includes("15"))
                this.weekDateRanges.push(range);
            else if(range.name.includes("Month"))
                this.monthDateRanges.push(range);
            else if(range.name.includes("Quarter"))
                this.quarterDateRanges.push(range);
            else if(range.name.includes("Year")
            || range.name === "YTD")
                this.yearDateRanges.push(range);
            else if(range.name === "Custom...")
                this.customDateRange = range;
        });
    }

    checkMaxDays() {
        if (this.maxDays == null) {
            if (this.maxDaysError)
                this.maxDaysError = '';
            return;
        }

        const numDaysSelected = this.selectedCustomRange.numberOfDays;
        if (numDaysSelected > this.maxDays) {
            this.maxDaysError = `Date range larger than the maximum date range of ${this.maxDays} days.`;
            this.cvLocalizationService.GetSection("Module").then(() => {
                if (this.maxDaysError) {
                    this.maxDaysError = this.cvLocalizationService.Format(
                        this.cvLocalizationService.getCachedText('Module', 'MaxDateRange',
                            "Date range larger than the maximum date range of {0} days."),
                        this.maxDays
                    );
                }
            });

            if (this.config.isCxOne) {
                this.selectedEndDate = new Date(this.selectedStartDate).addDays(this.maxDays - 1);
                const formattedStart = formatDate(this.selectedStartDate, this.CVAngularDateFormat, this.locale);
                const formattedEnd = formatDate(this.selectedEndDate, this.CVAngularDateFormat, this.locale);
                this.selectedCustomRange = new ModuleManagerDateRangeInfo();
                this.selectedCustomRange.name = `${formattedStart} - ${formattedEnd}`;
                this.selectedCustomRange.displayName = "Custom...";
                this.maxDaysError = '';
            }
        } else if (this.maxDaysError) {
            this.maxDaysError = '';
        }
    }

    onCustomDateRangeConfirmed() {
        if (this.maxDaysError)
            return;

        this.selectedDateRange = ModuleManagerDateRangeInfo.fromObject(this.selectedCustomRange);
        this.showCustom = false;
        this.showDropDown = false;
        this.selectedDateRangeChange.emit(this._selectedDateRange);
    }

    onDateRangeSelected(range: ModuleManagerDateRangeInfo) {
        if (range.name === "Custom...") {
            if (this.selectedCustomRange == null) {
                this.selectedCustomRange = ModuleManagerDateRangeInfo.fromObject(range);
                this.selectedStartDate = this.selectedCustomRange.startDate;
                this.selectedEndDate = this.selectedCustomRange.endDate;
                const formattedStart = formatDate(this.selectedStartDate, this.CVAngularDateFormat, this.locale);
                const formattedEnd = formatDate(this.selectedEndDate, this.CVAngularDateFormat, this.locale);
                this.selectedCustomRange.name = `${formattedStart} - ${formattedEnd}`;
            }
            this.showCustom = true;
            this.checkMaxDays();

            if (this.config.isCxOne)
                this.onCustomDateRangeConfirmed();
        } else {
            this.selectedDateRange = range;
            this.showDropDown = false;
            this.showCustom = false;
            this.selectedDateRangeChange.emit(this._selectedDateRange);
        }
    }
}
